import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * One "look" banner: an image with a button that opens a dialog listing the
 * look's products. Dialog, slideshow and add-to-cart mechanics mirror the
 * PDP shop-the-look block (assets/shop-the-look.js) minus the
 * recommendations fetch — products are server-rendered from section settings.
 */
class ShopTheLookBanner extends Component {
  /** @type {HTMLButtonElement | null} */
  #pendingButton = null;

  /** @type {number | null} */
  #pendingTimeout = null;

  /** @type {AbortController | null} */
  #swapAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('cart:updated', this.#handleCartUpdated);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdated);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    // Route Esc through the animated close so body scroll is restored.
    this.refs.dialog?.addEventListener('cancel', this.#handleCancel);
    // The product-group selector renders plain navigation links (the PDP
    // behavior); inside the dialog a sibling selection should swap the
    // product card in place instead of leaving the page.
    this.addEventListener('click', this.#handleGroupLinkClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:updated', this.#handleCartUpdated);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdated);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
    this.removeEventListener('click', this.#handleGroupLinkClick);
    this.#swapAbortController?.abort();
  }

  /**
   * Intercepts clicks on product-group selector links inside the dialog and
   * swaps the dialog's product card to the selected sibling in place,
   * mirroring the product-card colour-swap pattern (?view=card) with the
   * dialog's own ?view=stl-dialog alternate template.
   * @param {Event} event
   */
  #handleGroupLinkClick = (event) => {
    const link = /** @type {HTMLAnchorElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('product-group-selector a[href]')
    );
    if (!link) return;

    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement) || !dialog.contains(link)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#swapDialogProduct(link);
  };

  /**
   * @param {HTMLAnchorElement} link
   */
  async #swapDialogProduct(link) {
    const productEl = /** @type {HTMLElement | null} */ (
      link.closest('.shop-the-look-banners__product')
    );
    if (!productEl) return;

    this.#swapAbortController?.abort();
    this.#swapAbortController = new AbortController();

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('view', 'stl-dialog');

    productEl.classList.add('shop-the-look-banners__product--swapping');

    try {
      const response = await fetch(url.toString(), {
        signal: this.#swapAbortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newProduct = doc.querySelector('.shop-the-look-banners__product');
      // Guard against a malformed response so we never swap in a blank card.
      if (!(newProduct instanceof HTMLElement) || !newProduct.querySelector('form input[name="id"]')) {
        throw new Error('Malformed stl-dialog response');
      }

      if (!productEl.isConnected) return;

      // The fetched markup carries the renderer section's id; keep the
      // hosting section's id so post-add section rendering stays correct.
      const oldFormComponent = productEl.querySelector('product-form-component');
      const newFormComponent = newProduct.querySelector('product-form-component');
      const sectionId = oldFormComponent?.getAttribute('data-section-id');
      if (sectionId && newFormComponent) {
        newFormComponent.setAttribute('data-section-id', sectionId);
      }

      productEl.replaceWith(newProduct);
      this.#initSlideshows();
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') {
        // Fall back to normal navigation so the click is never a dead end.
        window.location.assign(link.href);
      }
    } finally {
      productEl.classList.remove('shop-the-look-banners__product--swapping');
    }
  }

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog(event);
  };

  /** @param {Event} event */
  #handleCartUpdated = (event) => {
    if (!this.#pendingButton) return;
    const didError = /** @type {any} */ (event).detail?.data?.didError;
    if (didError) {
      this.#handleCartError();
      return;
    }
    this.#markButtonAdded(this.#pendingButton);
    this.#pendingButton = null;
    // Keep the dialog open and show inline "Added!" feedback on the CTA
    // instead of opening the cart and closing this view. Monster Cart Upsells
    // auto-opens its drawer after every add, so close it back down here.
    this.#closeUpsellCart();
  };

  /**
   * Closes the Monster Cart Upsells drawer if the app is present, so adding a
   * product from within this dialog gives inline CTA feedback only.
   */
  #closeUpsellCart() {
    const closeCart = /** @type {(() => void) | undefined} */ (
      /** @type {any} */ (window).closeCart
    );
    if (typeof closeCart !== 'function') return;
    // The app opens its drawer asynchronously after the add request resolves;
    // close on the next frames to win the race against its open animation.
    requestAnimationFrame(() => {
      closeCart();
      requestAnimationFrame(() => closeCart());
    });
  }

  #handleCartError = () => {
    if (!this.#pendingButton) return;
    this.#pendingButton.classList.remove('shop-the-look-banners__add-to-cart--loading');
    this.#pendingButton.disabled = false;
    this.#pendingButton = null;
    if (this.#pendingTimeout) {
      clearTimeout(this.#pendingTimeout);
      this.#pendingTimeout = null;
    }
  };

  /** @param {Event} _event */
  openDialog(_event) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.refs.dialog);
    if (!dialog) return;

    dialog.classList.remove('is-closing');
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dialog.classList.add('is-active');
      });
    });

    this.#initSlideshows();
  }

  /**
   * Alternative banner action (coming soon pages): scroll to the section
   * holding the page's sign-up form instead of opening the dialog. The footer
   * newsletter form lives outside <main>, so only page content is matched.
   * @param {Event} _event
   */
  scrollToSignupForm(_event) {
    const form = document.querySelector('main [class*="klaviyo-form-"]');
    if (!form) return;

    const target = form.closest('.shopify-section') ?? form;
    const header = document.getElementById('header-component');
    const offset = header?.getBoundingClientRect().height ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  /** @param {Event} _event */
  closeDialog(_event) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.refs.dialog);
    if (!dialog || !dialog.open) return;

    dialog.classList.add('is-closing');
    dialog.classList.remove('is-active');

    const backdrop = dialog.querySelector('.shop-the-look-banners__dialog-backdrop');
    backdrop.addEventListener('transitionend', function handler() {
      backdrop.removeEventListener('transitionend', handler);
      dialog.classList.remove('is-closing');
      dialog.close();
      document.body.style.overflow = '';
    });
  }

  #initSlideshows() {
    const mediaWrappers = this.querySelectorAll('[ref="productSlideshow[]"]');
    mediaWrappers.forEach((wrapper) => {
      if (/** @type {HTMLElement} */ (wrapper).dataset.initialized) return;
      /** @type {HTMLElement} */ (wrapper).dataset.initialized = 'true';

      const scroller = wrapper.querySelector('.shop-the-look-banners__product-slideshow');
      if (!scroller) return;

      const images = scroller.querySelectorAll('.shop-the-look-banners__product-image');
      const prevBtn = /** @type {HTMLButtonElement | null} */ (
        wrapper.querySelector('[data-slide-prev]')
      );
      const nextBtn = /** @type {HTMLButtonElement | null} */ (
        wrapper.querySelector('[data-slide-next]')
      );

      if (images.length <= 1) {
        if (prevBtn) prevBtn.hidden = true;
        if (nextBtn) nextBtn.hidden = true;
        return;
      }

      const updateArrows = () => {
        const atStart = scroller.scrollLeft <= 1;
        const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
        if (prevBtn) prevBtn.disabled = atStart;
        if (nextBtn) nextBtn.disabled = atEnd;
      };

      /** @param {'next' | 'prev'} direction */
      const scrollBySlide = (direction) => {
        const firstImage = images[0];
        if (!firstImage) return;
        const imageWidth = firstImage.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(scroller).gap) || 1;
        scroller.scrollBy({
          left: direction === 'next' ? imageWidth + gap : -(imageWidth + gap),
          behavior: 'smooth',
        });
        setTimeout(updateArrows, 350);
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          scrollBySlide('prev');
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          scrollBySlide('next');
        });
      }

      scroller.addEventListener('scroll', () => updateArrows(), { passive: true });
      updateArrows();
    });
  }

  /**
   * Click handler on a size button in the dialog's size picker. Mirrors the
   * product-card size selector: writes the button's variant id into the
   * product form's hidden id input, re-enables the add-to-cart button (the
   * default variant may have been sold out), and moves the selected state.
   * @param {Event} event
   */
  selectSize(event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('.shop-the-look-banners__size-btn')
    );
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    const productEl = button.closest('.shop-the-look-banners__product');
    if (!variantId || !productEl) return;

    for (const sizeButton of productEl.querySelectorAll('.shop-the-look-banners__size-btn')) {
      sizeButton.classList.toggle('shop-the-look-banners__size-btn--selected', sizeButton === button);
    }

    const idInput = /** @type {HTMLInputElement | null} */ (
      productEl.querySelector('form input[name="id"]')
    );
    if (idInput) {
      idInput.value = variantId;
      idInput.disabled = false;
    }

    const addToCart = /** @type {HTMLButtonElement | null} */ (
      productEl.querySelector('.shop-the-look-banners__add-to-cart')
    );
    if (addToCart) {
      addToCart.dataset.variantId = variantId;
      addToCart.disabled = false;
      const addText = addToCart.dataset.addText;
      const isBusy =
        addToCart.classList.contains('shop-the-look-banners__add-to-cart--loading') ||
        addToCart.classList.contains('shop-the-look-banners__add-to-cart--added');
      if (addText && !isBusy) {
        addToCart.textContent = addText;
      }
    }
  }

  /**
   * Click handler on the submit button. Sets `data-skip-next-submit` on the
   * wrapping product-form-component so its own handleSubmit exits early, then
   * lets the form submit naturally for Monster Upsell to intercept. Matches
   * the pattern used by the PDP shop-the-look block to avoid double-adds.
   * @param {Event} event
   */
  handleAddToCart(event) {
    const button = /** @type {HTMLButtonElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('[data-variant-id]')
    );
    if (!button || button.disabled) return;

    const productForm = /** @type {HTMLElement | null} */ (
      button.closest('product-form-component')
    );
    if (productForm) {
      productForm.dataset.skipNextSubmit = 'true';
    }

    button.classList.add('shop-the-look-banners__add-to-cart--loading');

    this.#pendingButton = button;
    if (this.#pendingTimeout) clearTimeout(this.#pendingTimeout);
    this.#pendingTimeout = window.setTimeout(() => {
      if (this.#pendingButton === button) {
        button.classList.remove('shop-the-look-banners__add-to-cart--loading');
        this.#pendingButton = null;
      }
    }, 5000);
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #markButtonAdded(button) {
    if (this.#pendingTimeout) {
      clearTimeout(this.#pendingTimeout);
      this.#pendingTimeout = null;
    }
    button.classList.remove('shop-the-look-banners__add-to-cart--loading');
    button.classList.add('shop-the-look-banners__add-to-cart--added');

    const originalText = button.dataset.addText || button.textContent?.trim() || 'Add to cart';
    const addedText = button.dataset.addedText || 'Added';
    button.dataset.addText = originalText;
    button.textContent = addedText;

    setTimeout(() => {
      button.disabled = false;
      button.classList.remove('shop-the-look-banners__add-to-cart--added');
      button.textContent = originalText;
    }, 2000);
  }
}

if (!customElements.get('shop-the-look-banner')) {
  customElements.define('shop-the-look-banner', ShopTheLookBanner);
}
